// InvoiceComponent.js
import React, { useState } from "react";
import { IoIosClose } from "react-icons/io";
import { FaPrint, FaDownload } from "react-icons/fa";
import moment from "moment";
import PrintInvoice from "./PrintInvoice";
import CuModal from "../layouts/CuModal";
import CuSpinner from "./CuSpinner";

const InvoiceComponent = ({ closeInvoice, data, clinic_id }) => {
    const [openModal, setOpenModal] = useState(false);
    const [actionMode, setActionMode] = useState(null); // "print" or "download"
    const [isLoading, setIsLoading] = useState(false);

    const handlePrintClick = () => {
        setIsLoading(true);
        setActionMode("print");
        setOpenModal(true);
        // (Optional) simulate loading delay
        setTimeout(() => {
            setIsLoading(false);
        }, 3000);
    };

    const handleDownloadClick = () => {
        setIsLoading(true);
        setActionMode("download");
        setOpenModal(true);
        // (Optional) simulate loading delay
        setTimeout(() => {
            setIsLoading(false);
        }, 3000);
    };

    return (
        <div className="w-full h-full flex flex-col">
            {/* Header */}
            <div className="cu-visit-header px-4 h-[63px] border-b w-full flex justify-between items-center text-lg text-[#454545] font-bold">
                <span>Consultation Receipt</span>
                <IoIosClose
                    size={30}
                    onClick={closeInvoice}
                    className="cursor-pointer"
                />
            </div>

            {/* Invoice Details (General, Invoice, Payment, etc.) */}
            <div className="w-full flex-1 overflow-auto">
                {/* General Details */}
                <div className="w-full px-4 my-4">
                    <div className="font-bold text-sm">General Details</div>
                    <div className="w-full flex flex-col gap-2 mt-2">
                        <div className="flex w-full justify-between items-center text-sm">
                            <div>Date</div>
                            <div>
                                {moment(data?.date_created).format(
                                    "DD MMM yyyy"
                                )}
                            </div>
                        </div>
                        <div className="flex w-full justify-between items-center text-sm">
                            <div>Invoice No.</div>
                            <div>{data?.invoice_no}</div>
                        </div>
                    </div>
                </div>

                {/* Invoice Details */}
                <div className="w-full px-4 py-4 border-b">
                    <div className="font-bold text-sm">Invoice Details</div>
                    <div className="w-full flex flex-col gap-2 mt-2">
                        {data?.invoice_line_id?.map((item, i) => (
                            <div
                                key={i}
                                className="flex w-full justify-between items-center text-sm"
                            >
                                <div>{item?.line_service?.name_of_service}</div>
                                <div>₹ {item?.line_service?.price}</div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Total Section */}
                <div className="w-full px-4 py-4 border-b">
                    <div className="font-bold text-sm">Total</div>
                    <div className="w-full flex flex-col gap-2 mt-2">
                        <div className="flex w-full justify-between items-center text-sm">
                            <div>Amount</div>
                            <div>₹ {data?.invoice_total}</div>
                        </div>
                        <div className="flex w-full justify-between items-center text-sm">
                            <div>Discount</div>
                            <div>
                                {data?.discount}
                                {data?.discount_type === "percentage"
                                    ? "%"
                                    : "₹ "}
                            </div>
                        </div>
                        <div className="flex w-full justify-between items-center text-sm">
                            <div>Total</div>
                            <div>
                                ₹{" "}
                                {data?.discount_type === "percentage"
                                    ? data?.invoice_total -
                                      data?.invoice_total *
                                          (data?.discount / 100)
                                    : data?.invoice_total - data?.discount}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Payment Section */}
                <div className="w-full px-4 my-4">
                    <div className="font-bold text-sm">Payment</div>
                    <div className="w-full flex flex-col gap-2 mt-2">
                        <div className="flex w-full justify-between items-center text-sm">
                            <div>Type</div>
                            <div>
                                {data?.payment_id?.reduce(
                                    (acc, item) => acc + item?.received_payment,
                                    0
                                ) === data?.invoice_total
                                    ? "Paid"
                                    : "Partial"}
                            </div>
                        </div>

                        {data?.payment_id?.map((item, i) => (
                            <div
                                key={i}
                                className="flex w-full justify-between items-center text-sm"
                            >
                                <div>Payment {i + 1}</div>
                                <div>₹ {item?.received_payment}</div>
                            </div>
                        ))}

                        {data?.payment_id?.reduce(
                            (acc, item) => acc + item?.received_payment,
                            0
                        ) < data?.invoice_total && (
                            <div className="flex w-full justify-between items-center text-sm">
                                <div>Remaining Amount</div>
                                <div>
                                    ₹{" "}
                                    {data?.invoice_total -
                                        data?.payment_id?.reduce(
                                            (acc, item) =>
                                                acc + item?.received_payment,
                                            0
                                        )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Two Action Buttons */}
            <div className="w-full py-2 px-4 text-center flex justify-around gap-4">
                <button
                    onClick={handlePrintClick}
                    className="flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-bold rounded-md px-4 py-2 transition duration-300 ease-in-out"
                >
                    {isLoading && actionMode === "print" ? (
                        <>
                            <CuSpinner />
                            <span>Preparing Print...</span>
                        </>
                    ) : (
                        <>
                            <FaPrint className="w-5 h-5" />
                            <span>Print Invoice</span>
                        </>
                    )}
                </button>
                <button
                    onClick={handleDownloadClick}
                    className="flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-bold rounded-md px-4 py-2 transition duration-300 ease-in-out"
                >
                    {isLoading && actionMode === "download" ? (
                        <>
                            <CuSpinner />
                            <span>Preparing PDF...</span>
                        </>
                    ) : (
                        <>
                            <FaDownload className="w-5 h-5" />
                            <span>Download Invoice</span>
                        </>
                    )}
                </button>
            </div>

            {/* Modal for Invoice actions */}
            {openModal && (
                <CuModal closeModal={() => setOpenModal(false)}>
                    <PrintInvoice
                        clinic_id={clinic_id}
                        invoice_id={data?.id}
                        mode={actionMode} // "print" or "download"
                        setOpenModal={setOpenModal}
                    />
                </CuModal>
            )}
        </div>
    );
};

export default InvoiceComponent;
